import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { InputField } from "@/components/input-field/InputField";
import { SelectComponent } from "@/components/select/Select";
import { TextArea } from "@/components/text-area/TextArea";
import { PermissionCode } from "@/constants/permissions";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import type {
  NotificationTypeDetail,
  PatchNotificationTypeData,
  NotificationTypeListItem,
} from "@/services/notification-types.service";
import { getNotificationTypes } from "@/services/notification-types.service";
import { AppColors } from "@/styles/colors";
import { Card, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  fetchEntityStatusName,
  fetchNotificationTypeDetailHandler,
  updateNotificationTypeHandler,
} from "./schema/handler-functions";
import { notificationTypeUpdateSchema } from "./schema/notification-type.schema";
import { NotificationBodyPreviewCard } from "@/components/common/section-components/card/NotificationBodyPreviewCard";
import {
  buildCodeIndexMap,
  buildPatchedBody,
  buildPreviewBody,
  pickFirst,
  splitBody,
} from "./schema/aux-functions";

export const NotificationTypeDetailPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { id } = useParams();
  const navigate = useNavigate();
  const { state: routeState } = useLocation();
  const { fetchPermissions, hasWriteAccess } = useRoles();

  const canWrite = useMemo(() => {
    return !!hasWriteAccess?.(PermissionCode.NOTIFICATION_TYPES);
  }, [hasWriteAccess]);

  const [loading, setLoading] = useState<boolean>(false);
  const [detail, setDetail] = useState<NotificationTypeDetail | null>(null);
  const [statusName, setStatusName] = useState<string | null>(null);
  const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
  const [loadingStatusName, setLoadingStatusName] = useState<boolean>(false);

  const [resolvedCodigo, setResolvedCodigo] = useState<string | null>(null);
  const [resolvedTipoLabel, setResolvedTipoLabel] = useState<string | null>(
    null,
  );

  const listItem: NotificationTypeListItem | null =
    routeState?.item && typeof routeState.item === "object"
      ? routeState.item
      : null;

  const {
    getValues,
    setValue,
    register,
    control,
    formState: { errors },
  } = useForm<FieldValues>({
    resolver: zodResolver(notificationTypeUpdateSchema) as any,
    defaultValues: {
      titulo: "",
      cuerpo: "",
    },
  });

  const watchedValues = useWatch({ control });

  const moduleLabel = useMemo(() => {
    const code = resolvedCodigo ?? listItem?.codigo;
    if (!code) return "-";
    return buildCodeIndexMap().get(code) ?? "OTROS";
  }, [listItem?.codigo, resolvedCodigo]);

  const parsedId = useMemo(() => Number(id), [id]);

  const bodyHasParams = Boolean(
    detail?.cantidadParametros && detail.cantidadParametros > 0,
  );

  const bodyTokens = useMemo(() => {
    if (!detail?.cuerpo) return [];
    return splitBody(detail.cuerpo);
  }, [detail?.cuerpo]);

  const initialEditableSuffix = useMemo(() => {
    if (!detail?.cuerpo) return "";
    if (!bodyTokens.length) return detail.cuerpo;
    const lastText = [...bodyTokens].reverse().find((t) => t.type === "text");
    return lastText?.value ?? "";
  }, [detail?.cuerpo, bodyTokens]);

  useEffect(() => {
    if (!Number.isFinite(parsedId)) return;

    if (isActive) {
      fetchNotificationTypeDetailHandler({
        id: parsedId,
        setLoading,
        setDetail,
        navigate,
        fetchPermissions,
        routeState,
      });
    }
  }, [parsedId, isActive]);

  useEffect(() => {
    if (!detail) return;
    setValue("titulo", detail.titulo ?? "");

    if (detail.cantidadParametros && detail.cantidadParametros > 0) {
      setValue("cuerpo", initialEditableSuffix);
    } else {
      setValue("cuerpo", detail.cuerpo ?? "");
    }

    const tipoLabel =
      resolvedTipoLabel ?? listItem?.modulo ?? detail.modulo ?? "-";
    setValue("tipoUi", [tipoLabel]);
    setValue("moduloUi", [moduleLabel]);
  }, [detail, initialEditableSuffix]);

  useEffect(() => {
    if (!detail) return;

    const fromRoute = listItem?.codigo ?? null;
    const fromRouteTipo = listItem?.modulo ?? null;

    if (fromRoute) setResolvedCodigo(fromRoute);
    if (fromRouteTipo) setResolvedTipoLabel(fromRouteTipo);

    if (fromRoute) return;

    const run = async () => {
      try {
        const list = await getNotificationTypes();
        const match = (list ?? []).find((x) => x.id === detail.id);
        if (match?.codigo) setResolvedCodigo(match.codigo);
        if (match?.modulo) setResolvedTipoLabel(match.modulo);
      } catch {
        // ignore
      }
    };

    run();
  }, [detail?.id, listItem?.codigo, listItem?.modulo]);

  useEffect(() => {
    setValue("estadoUi", [statusName ?? "-"]);
  }, [statusName]);

  useEffect(() => {
    const run = async () => {
      if (!detail) return;
      if (detail.idEntidad === null || detail.idEstado === null)
        return setStatusName(null);

      const idEntidad = Number(detail.idEntidad);
      const idEstado = Number(detail.idEstado);
      if (!Number.isFinite(idEntidad)) return setStatusName(null);
      if (!Number.isFinite(idEstado)) return setStatusName(null);

      try {
        setLoadingStatusName(true);
        const name = await fetchEntityStatusName({ idEntidad, idEstado });
        setStatusName(name);
      } catch {
        setStatusName(null);
      } finally {
        setLoadingStatusName(false);
      }
    };

    run();
  }, [detail?.idEntidad, detail?.idEstado]);

  const previewBody = useMemo(() => {
    const current = watchedValues?.cuerpo ?? "";
    return buildPreviewBody({
      detail,
      bodyHasParams,
      splitBody,
      newSuffix: String(current),
    });
  }, [watchedValues?.cuerpo, detail?.cuerpo, bodyHasParams]);

  const hasNoFilledValues = useMemo(() => {
    const { titulo, cuerpo } = watchedValues || {};
    return !titulo || !cuerpo;
  }, [watchedValues]);

  const handleCancel = () => {
    navigate(AppRoutes.NOTIFICATIONS);
  };

  const handleSave = async () => {
    if (!detail) return;
    const values = getValues();

    const data: PatchNotificationTypeData = {
      titulo: String(values.titulo ?? "").trim(),
      cuerpo: bodyHasParams
        ? buildPatchedBody({
            detail,
            bodyHasParams,
            splitBody,
            newSuffix: String(values.cuerpo ?? ""),
          })
        : String(values.cuerpo ?? "").trim(),
      tieneIdentificador: Boolean(detail.tieneIdentificador),
      cantidadParametros: Number(detail.cantidadParametros ?? 0),
    };

    await updateNotificationTypeHandler({
      id: detail.id,
      data,
      setLoading: setLoadingUpdate,
      navigate,
      fetchPermissions,
      routeState,
    });
  };

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      data-testid="notification-type-detail-page"
    >
      <SectionHeader
        title={canWrite ? "Editar notificación" : "Detalle notificación"}
        showButtons={canWrite}
        addButtonLabel={canWrite ? "Guardar" : undefined}
        addButtonIcon={canWrite ? <Save /> : undefined}
        cancelButtonLabel={canWrite ? "Cancelar" : undefined}
        handleAdd={canWrite ? handleSave : undefined}
        handleCancel={canWrite ? handleCancel : undefined}
        disableAdd={loading || loadingUpdate || hasNoFilledValues || !canWrite}
        loading={loading || loadingUpdate}
        backButton={!canWrite}
        onBack={() => navigate(AppRoutes.NOTIFICATIONS)}
      />

      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(4, 1fr)" gap="16px">
            <GridItem>
              <SelectComponent
                control={control}
                name="moduloUi"
                items={[
                  {
                    label: String(
                      pickFirst(watchedValues?.moduloUi) ?? moduleLabel ?? "-",
                    ),
                    value: String(
                      pickFirst(watchedValues?.moduloUi) ?? moduleLabel ?? "-",
                    ),
                  },
                ]}
                labelText="Módulo"
                placeholder="-"
                clearable={false}
                disabled
                minHeight="40px"
              />
            </GridItem>
            <GridItem>
              <SelectComponent
                control={control}
                name="tipoUi"
                items={[
                  {
                    label: String(
                      pickFirst(watchedValues?.tipoUi) ??
                        resolvedTipoLabel ??
                        listItem?.modulo ??
                        detail?.modulo ??
                        "-",
                    ),
                    value: String(
                      pickFirst(watchedValues?.tipoUi) ??
                        resolvedTipoLabel ??
                        listItem?.modulo ??
                        detail?.modulo ??
                        "-",
                    ),
                  },
                ]}
                labelText="Tipo"
                placeholder="-"
                clearable={false}
                disabled
                minHeight="40px"
              />
            </GridItem>
            <GridItem>
              <SelectComponent
                control={control}
                name="estadoUi"
                items={[
                  {
                    label: String(
                      pickFirst(watchedValues?.estadoUi) ?? statusName ?? "-",
                    ),
                    value: String(
                      pickFirst(watchedValues?.estadoUi) ?? statusName ?? "-",
                    ),
                  },
                ]}
                labelText="Estado"
                placeholder="-"
                clearable={false}
                loading={loadingStatusName}
                disabled
                minHeight="40px"
              />
            </GridItem>
            <GridItem>
              <InputField
                control={control}
                inputName="titulo"
                labelText="Título (máx. 36 caracteres)"
                placeholder="Ingresar título"
                maxLength={36}
                disabled={loading || loadingUpdate || !canWrite}
              />
            </GridItem>
          </Grid>

          <Flex flexDir="column" gap="12px" marginTop="24px">
            <Text fontWeight={700} fontSize="20px">
              Mensaje de notificación
            </Text>

            {bodyHasParams ? (
              <>
                {/* Quitar la vista previa a futuro y cambiarlo por nuevo componente a discutir con Agus */}
                <NotificationBodyPreviewCard previewBody={previewBody} />

                <Text fontWeight={600} fontSize="16px">
                  Cuerpo
                </Text>
                <TextArea
                  errors={errors}
                  inputName="cuerpo"
                  register={register}
                  placeholder="Editar texto"
                  helperText="Máximo 80 caracteres"
                  maxLength={80}
                  disabled={loading || loadingUpdate || !canWrite}
                />
              </>
            ) : (
              <TextArea
                errors={errors}
                inputName="cuerpo"
                register={register}
                placeholder="Ingresar mensaje"
                helperText="Máximo 80 caracteres"
                maxLength={80}
                disabled={loading || loadingUpdate || !canWrite}
              />
            )}
          </Flex>
        </Card.Body>
      </Card.Root>
    </Flex>
  );
};
